/** Optional emitter glow, composited in the main pass and measured by its GPU timestamp. */
import type { RenderFrame } from '../frame';

export type LightEmitter = {
  x: number;
  y: number;
  radius: number;
  r: number;
  g: number;
  b: number;
  intensity: number;
};

/** CPU reference for lava / muzzle / explosion falloff. */
export function emitterContribution(
  emitters: LightEmitter[],
  wx: number,
  wy: number,
): { r: number; g: number; b: number } {
  let r = 0,
    g = 0,
    b = 0;
  for (const e of emitters) {
    const dist = Math.hypot(wx - e.x, wy - e.y);
    const t = 1 - Math.min(1, dist / Math.max(e.radius, 0.01));
    if (t <= 0) continue;
    const w = e.intensity * t * t;
    r += e.r * w;
    g += e.g * w;
    b += e.b * w;
  }
  return { r: Math.min(1, r), g: Math.min(1, g), b: Math.min(1, b) };
}

export type LightingPass = {
  draw: (
    pass: GPURenderPass,
    frame: RenderFrame,
    width: number,
    height: number,
    pixelRatio: number,
  ) => void;
  destroy: () => void;
  kind: 'glow';
};

const GLOW_WGSL = /* wgsl */ `
struct Camera {
  x: f32,
  y: f32,
  zoom: f32,
  pad: f32,
  view: vec2f,
  shake: vec2f,
}
struct Emitter {
  pos: vec2f,
  radius: f32,
  intensity: f32,
  color: vec3f,
  pad: f32,
}
struct Lights {
  count: u32,
  pad0: u32,
  pad1: u32,
  pad2: u32,
  items: array<Emitter, 16>,
}
@group(0) @binding(0) var<uniform> camera: Camera;
@group(0) @binding(1) var<uniform> lights: Lights;

@vertex
fn vs(@builtin(vertex_index) vid: u32) -> @builtin(position) vec4f {
  var p = array<vec2f, 3>(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  return vec4f(p[vid], 0.0, 1.0);
}

@fragment
fn fs(@builtin(position) pos: vec4f) -> @location(0) vec4f {
  let ppm = camera.zoom;
  let wx = (pos.x - camera.shake.x - camera.view.x * 0.5) / ppm + camera.x;
  let wy = (camera.view.y * 0.5 - pos.y + camera.shake.y) / ppm + camera.y;
  var acc = vec3f(0.0);
  for (var i = 0u; i < lights.count; i++) {
    let e = lights.items[i];
    let d = length(vec2f(wx, wy) - e.pos);
    let t = 1.0 - clamp(d / max(e.radius, 0.01), 0.0, 1.0);
    acc += e.color * e.intensity * t * t;
  }
  let warmth = acc * 0.55;
  return vec4f(warmth, min(0.45, length(acc) * 0.35));
}
`;

export function createLightingPass(device: GPUDevice, format: string): LightingPass {
  const module = device.createShaderModule({ code: GLOW_WGSL });
  const pipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: { module, entryPoint: 'vs' },
    fragment: {
      module,
      entryPoint: 'fs',
      targets: [
        {
          format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
          },
        },
      ],
    },
    primitive: { topology: 'triangle-list' },
  });
  const cameraBuffer = device.createBuffer({
    size: 32,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const lightsBuffer = device.createBuffer({
    size: 16 + 16 * 32,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const bind = device.createBindGroup({
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: cameraBuffer } },
      { binding: 1, resource: { buffer: lightsBuffer } },
    ],
  });
  const camera = new Float32Array(8);
  const lights = new ArrayBuffer(16 + 16 * 32);
  const view = new DataView(lights);
  let disposed = false;
  return {
    kind: 'glow',
    draw(pass, frame, width, height, pixelRatio) {
      if (disposed || !frame.lights?.length) return;
      camera[0] = frame.camera.x;
      camera[1] = frame.camera.y;
      camera[2] = frame.camera.zoom * pixelRatio;
      camera[4] = width;
      camera[5] = height;
      camera[6] = frame.camera.shakeX * pixelRatio;
      camera[7] = frame.camera.shakeY * pixelRatio;
      device.queue.writeBuffer(cameraBuffer, 0, camera);
      const count = Math.min(16, frame.lights.length);
      view.setUint32(0, count, true);
      for (let i = 0; i < count; i++) {
        const e = frame.lights[i]!;
        const o = 16 + i * 32;
        view.setFloat32(o, e.x, true);
        view.setFloat32(o + 4, e.y, true);
        view.setFloat32(o + 8, e.radius, true);
        view.setFloat32(o + 12, e.intensity, true);
        view.setFloat32(o + 16, e.r, true);
        view.setFloat32(o + 20, e.g, true);
        view.setFloat32(o + 24, e.b, true);
      }
      device.queue.writeBuffer(lightsBuffer, 0, lights);
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bind);
      pass.draw(3);
    },
    destroy() {
      if (disposed) return;
      disposed = true;
      cameraBuffer.destroy();
      lightsBuffer.destroy();
    },
  };
}
